'use client';

import { useEffect, useRef, useState } from 'react';
import { videoReplicationsApi, type KagemaneQuote, type KagemaneBillingMode, type VideoReplicationModel } from '@/lib/api/video-replications';

export type KagemaneQuoteStatus = 'idle' | 'loading' | 'error' | 'ready';

/** Show one quote for the current account and parameters; never reuse a previous model's price. */
export function useKagemaneQuote({ ownerId, videoModel, resolution, duration, enabled, refreshKey, billingMode = 'dobi' }: {
  ownerId?: string; videoModel: VideoReplicationModel; resolution: string; duration?: number;
  enabled: boolean; refreshKey: number;
  billingMode?: KagemaneBillingMode;
}): { quote: KagemaneQuote | null; status: KagemaneQuoteStatus } {
  const [snapshot, setSnapshot] = useState<{ key: string; quote: KagemaneQuote | null; status: 'ready' | 'error' } | null>(null);
  const validDuration = typeof duration === 'number' && Number.isFinite(duration) && duration >= 4 && duration <= 30;
  const canQuote = Boolean(enabled && ownerId && validDuration);
  const parameters = JSON.stringify([ownerId, videoModel, resolution, duration, enabled, refreshKey, billingMode]);
  const revision = useRef({ parameters, number: 0 });
  if (revision.current.parameters !== parameters) {
    revision.current = { parameters, number: revision.current.number + 1 };
  }
  const key = JSON.stringify([parameters, revision.current.number]);
  useEffect(() => {
    let active = true;
    if (!canQuote) return;
    async function load() {
      try {
        const result = await videoReplicationsApi.quote(videoModel, resolution, duration!, billingMode);
        const quote = result.data;
        if (!result.success || !quote || quote.video_model !== videoModel || quote.resolution !== resolution) {
          throw new Error('kagemane_quote_unavailable');
        }
        if (billingMode === 'monthly_gift' && (quote.gift_applied !== true || quote.pricing_mode !== 'fixed'
          || !['480p', '720p', '768p', '1080p'].includes(resolution))) {
          throw new Error('kagemane_gift_quote_unavailable');
        }
        if (quote.pricing_mode === 'unplanned') {
          if (!['seedance-2.0', 'seedance-2.5'].includes(videoModel) || quote.source_duration_sec !== duration
            || quote.billable_seconds !== null || quote.credits !== null || quote.estimate_min !== null || quote.estimate_max !== null) {
            throw new Error('kagemane_quote_unavailable');
          }
        } else if (quote.billable_seconds !== Math.ceil(duration!) || !Number.isSafeInteger(quote.credits) || quote.credits < (billingMode === 'monthly_gift' ? 0 : 1)
          || !Number.isSafeInteger(quote.estimate_min) || quote.estimate_min < (billingMode === 'monthly_gift' ? 0 : 1)
          || !Number.isSafeInteger(quote.estimate_max) || quote.estimate_max < quote.estimate_min
          || quote.credits !== quote.estimate_max
          || !['fixed', 'range'].includes(quote.pricing_mode)
          || (quote.pricing_mode === 'fixed' && (quote.credits !== quote.estimate_min || quote.credits !== quote.estimate_max))) {
          throw new Error('kagemane_quote_unavailable');
        }
        if (active) setSnapshot({ key, quote, status: 'ready' });
      } catch { if (active) setSnapshot({ key, quote: null, status: 'error' }); }
    }
    void load();
    return () => { active = false; };
  }, [key, canQuote, videoModel, resolution, duration, billingMode]);
  if (!canQuote) return { quote: null, status: 'idle' };
  return snapshot?.key === key ? { quote: snapshot.quote, status: snapshot.status } : { quote: null, status: 'loading' };
}
